import type { ComponentType } from 'react';
import type { LazyPage } from '@/config/types';
import { lazyPage } from '@/utils/lazyPage';

function contentPage(load: () => Promise<ComponentType>): LazyPage {
  return lazyPage(async () => {
    const Page = await load();
    return {
      default: function BusinessContentPage() {
        return (
          <div data-testid="business-content" style={{ minWidth: 0 }}>
            <Page />
          </div>
        );
      },
    };
  });
}

// 仅有导航骨架、尚无业务内容的二级页面共用同一占位页。
export const gatewayPlaceholderPaths = [
  '/overview/usage',
  '/cluster/clusters',
  '/cluster/rollouts',
  '/routing/channels',
  '/routing/models',
  '/routing/policies',
  '/routing/quotas',
  '/monitoring/request-logs',
  '/monitoring/health',
  '/system/backup',
] as const;

const gatewayPlaceholderPages = Object.fromEntries(
  gatewayPlaceholderPaths.map((path) => [
    path,
    contentPage(() =>
      import('./gateway/GatewayPlaceholderPage').then(
        (module) => module.GatewayPlaceholderPage,
      ),
    ),
  ]),
);

// 只替换 Outlet 内的业务内容，外层导航、标签与账户页面保持现有实现。
export const businessPages: Record<string, LazyPage> = {
  ...gatewayPlaceholderPages,
  '/access/api-keys': contentPage(() =>
    import('./gateway/api-keys/ApiKeysPage').then((module) => module.ApiKeysPage),
  ),
  '/routing/accounts': contentPage(() =>
    import('./gateway/accounts/AccountsPage').then((module) => module.AccountsPage),
  ),
  '/cluster/instances': contentPage(() =>
    import('./gateway/instances/InstancesPage').then((module) => module.InstancesPage),
  ),
  '/cluster/servers': contentPage(() =>
    import('./gateway/servers/ServersPage').then((module) => module.ServersPage),
  ),
  '/dashboard': contentPage(() =>
    import('./dashboard/DashboardPage').then((module) => module.DashboardPage),
  ),
  '/organization/users': contentPage(() =>
    import('./organization/UsersPage').then((module) => module.UsersPage),
  ),
  '/access/roles': contentPage(() =>
    import('./access/RolesPage').then((module) => module.RolesPage),
  ),
  '/organization/departments': contentPage(() =>
    import('./departments/DepartmentsPage').then((module) => module.DepartmentsPage),
  ),
  '/organization/positions': contentPage(() =>
    import('./positions/PositionsPage').then((module) => module.PositionsPage),
  ),
  '/system/dictionaries': contentPage(() =>
    import('./dictionaries/DictionariesPage').then((module) => module.DictionariesPage),
  ),
  '/system/announcements': contentPage(() =>
    import('./announcements/AnnouncementsPage').then(
      (module) => module.AnnouncementsPage,
    ),
  ),
  '/system/settings': contentPage(() =>
    import('./system/PlatformSettingsPage').then(
      (module) => module.PlatformSettingsPage,
    ),
  ),
  '/operations/login-logs': contentPage(() =>
    import('./operations/LoginLogPage').then((module) => module.LoginLogPage),
  ),
  '/operations/audit-logs': contentPage(() =>
    import('./operations/AuditLogPage').then((module) => module.AuditLogPage),
  ),
  '/system/about': contentPage(() =>
    import('./system/AboutSystemPage').then((module) => module.AboutSystemPage),
  ),
  '/system/help/errors': contentPage(() =>
    import('./system/help/HelpErrorsPage').then((module) => module.HelpErrorsPage),
  ),
  '/security/events': contentPage(() =>
    import('./security/SecurityEventsPage').then((module) => module.SecurityEventsPage),
  ),
  '/security/sessions': contentPage(() =>
    import('./security/SecuritySessionsPage').then(
      (module) => module.SecuritySessionsPage,
    ),
  ),
  '/security/policies': contentPage(() =>
    import('./security/AccessPoliciesPage').then((module) => module.AccessPoliciesPage),
  ),
};
